import React, { useCallback, useEffect, useState } from 'react';
import { Bell, CircleHelp, LogOut, Menu } from "lucide-react";
import sessionDB from "../../utils/sessionDB";
import { Trans, useTranslation } from "react-i18next";
import authApi from "../../utils/api";
import { UserMenu } from "./UserMenu";
import { NotificationsModal } from "./NotificationsModal";
import { FaqModal } from "./FaqModal";
import { notificationService } from "../../services/notificationService";

export function Header({ showMenuToggle = true, showBottomBorder = true }) {
  const { t } = useTranslation();
  const tenant = sessionDB.getTenantObj();
  const tenantName = tenant.tenantName || "";
  const [logoutPending, setLogoutPending] = useState(false);

  const handleLogout = async () => {
    if (logoutPending) return;

    setLogoutPending(true);
    const refreshToken = sessionDB.getRefreshToken();

    try {
      await fetch(authApi._url("/saas/login/logout"), {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ refreshToken }),
      });
    } catch {
      // The local session still needs to be removed when the server cannot be
      // reached; the refresh token will no longer be reused by this browser.
    } finally {
      // Signing out locally must succeed even when the logout endpoint is
      // unavailable. The event also resets App's in-memory session state.
      sessionDB.clearSession();
      window.dispatchEvent(new CustomEvent("hrms-logout"));
    }
  };

  const [notificationsOpen, setNotificationsOpen] = useState(false);
  const [faqOpen, setFaqOpen] = useState(false);
  const [unreadCount, setUnreadCount] = useState(0);
  const loadUnreadCount = useCallback(async () => {
    try {
      const response = await notificationService.getUnreadCount();
      const value = response?.data?.count ?? response?.data?.unreadCount ?? response?.count ?? response?.unreadCount ?? response?.data ?? response ?? 0;
      setUnreadCount(Math.max(0, Number(value) || 0));
    } catch {
      // A count failure should not disrupt the rest of the global header.
    }
  }, []);

  useEffect(() => { loadUnreadCount(); }, [loadUnreadCount]);

  return (
    <header 
    className={`hrms-shell__header relative !grid h-auto! min-h-[112px] w-full grid-cols-1 grid-rows-[48px_32px] items-center gap-y-2 px-4! py-2! sm:px-6! bg-primary shrink-0 ${
      showBottomBorder ? "border-b border-[#1E40AF]" : "border-b-0"
    }`}
    style={showBottomBorder ? undefined : { borderBottom: "none" }}>
      <div className="col-start-1 row-start-1 flex min-w-0 items-center justify-self-start self-start gap-2 sm:gap-4">
          <div className="h-9 w-9 rounded-[8px] overflow-hidden shrink-0 bg-white sm:h-12 sm:w-12">
                <img
                  src="/images/karya1.1.jpg"
                  alt={t("common.companyLogo")}
                  className="h-full w-full object-cover block" />
          </div>
           <div className="flex h-9 flex-col justify-center leading-none min-w-0 sm:h-12">
                <span className="text-lg font-bold text-white truncate sm:text-2xl"><Trans i18nKey="common.karya" /></span>
                <span className="mt-0.5 text-[8px] uppercase tracking-[0.2em] text-white/70 sm:text-[10px] sm:tracking-[0.3em]">
                  <Trans i18nKey="common.saas" />
                </span>
            </div>
      </div>
      <div className="col-start-1 row-start-2 flex h-8 items-center justify-self-start self-end">
        {showMenuToggle && <button
          type="button"
          onClick={() => window.dispatchEvent(new CustomEvent("toggle-sidebar"))}
          className="grid h-8 w-8 shrink-0 place-items-center rounded-lg per-file-hint hover:bg-workspace/40 cursor-pointer"
          aria-label={t("common.openMenu")}
        >
          <Menu className="h-5 w-5" />
        </button>}
      </div>

      {/* Centered tenant name, absolutely positioned so it's centered on the whole header — only visible on desktop */}
      <div className="pointer-events-none absolute left-1/2 top-8 hidden -translate-x-1/2 -translate-y-1/2 md:block">
        <span className="whitespace-nowrap text-3xl font-bold leading-none text-white">
         {tenantName}
        </span>
      </div>
        
      <div className="absolute right-4 top-2 flex min-w-0 justify-end sm:right-6">
        <img
          src={tenant.tenantLogo}
          alt={tenantName || "Tenant logo"}
          className="h-11 w-auto max-w-[140px] shrink-0 object-contain sm:h-14 sm:max-w-[200px]"
        />
      </div>
      <div className="absolute bottom-2 right-4 flex h-8 items-center justify-end gap-1.5 sm:right-6 [&_select]:h-8 [&_select]:px-1.5 [&_select]:text-xs">
        <button
          type="button"
          onClick={() => setFaqOpen(true)}
          className="grid h-8 w-8 place-items-center rounded-full text-white hover:bg-workspace/40"
          aria-label="Open help and FAQs"
          title="Help & FAQs"
        >
          <CircleHelp className="h-4 w-4" />
        </button>
        <button
          type="button"
          onClick={() => setNotificationsOpen(true)}
          className="relative grid h-8 w-8 place-items-center rounded-full hover:bg-workspace/40"
          aria-label={t("common.notifications")}
        >
          <Bell className="h-4 w-4 text-white" />
          {unreadCount > 0 && <span className="absolute -right-1 -top-1 grid min-h-4 min-w-4 place-items-center rounded-full bg-error px-1 text-[9px] font-bold leading-none text-white ring-2 ring-primary" aria-label={t("notifications.unreadCount", { count: unreadCount })}>{unreadCount > 99 ? "99+" : unreadCount}</span>}
        </button>
       
        <UserMenu />
        <button
          type="button"
          onClick={handleLogout}
          disabled={logoutPending}
          aria-busy={logoutPending}
          aria-label={logoutPending ? "Signing out..." : "Sign out"}
          title={logoutPending ? "Signing out..." : "Sign out"}
          className="grid h-8 w-8 place-items-center rounded-full text-white hover:bg-workspace/40 focus-visible:outline focus-visible:outline-2 focus-visible:outline-white disabled:cursor-not-allowed disabled:opacity-60"
        >
          <LogOut className="h-4 w-4" aria-hidden="true" />
        </button>
      </div>
      <FaqModal open={faqOpen} onOpenChange={setFaqOpen} />
      <NotificationsModal open={notificationsOpen} onOpenChange={setNotificationsOpen} onUnreadCountChange={setUnreadCount} />
    </header>
  );
}
